import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { FlatList, ListRenderItemInfo, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import CheckMarkImage from 'assets/images/svgs/ico_checkmark.svg';

import gStyles from 'config/styles';

import {
    selectLayoutConfig,
    selectScreenReaderEnabled,
} from 'store/config/selectors';
import { setEpgSelectedLayout } from 'store/epg/actions';
import { EpgLayout } from 'store/epg/reducer.types';
import { getEpgSelectedLayout } from 'store/epg/selectors';

import useHeaderConfig from 'utils/hooks/useHeaderConfig';

import SettingsRowItem from '../../components/RowItem';
import {
    SETTINGS_DISPLAY_EPG_OPTIONS_CLASSIC,
    SETTINGS_DISPLAY_EPG_OPTIONS_NEW,
} from '../../languagesConst';
import { SimpleSettingsItem } from '../../types';

type EpgLayoutSettingsItem = SimpleSettingsItem & {
    type: EpgLayout;
};

function EpgDisplayOptions() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { background } = useSelector(selectLayoutConfig);
    const epgLayout = useSelector(getEpgSelectedLayout);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const [selectedEpgLayout, setSelectedEpgLayout] = useState(epgLayout);

    useHeaderConfig({ isRoot: false });

    const listStyle = useMemo(
        () => ({
            backgroundColor: background,
        }),
        [background],
    );

    const handleChangeAudio = useCallback(
        ({ type }: EpgLayoutSettingsItem) => {
            dispatch(setEpgSelectedLayout(type));
        },
        [dispatch],
    );

    const epgLayoutOptions = useMemo<EpgLayoutSettingsItem[]>(
        () => [
            {
                id: SETTINGS_DISPLAY_EPG_OPTIONS_CLASSIC,
                title: SETTINGS_DISPLAY_EPG_OPTIONS_CLASSIC,
                type: EpgLayout.GRID,
            },
            {
                id: SETTINGS_DISPLAY_EPG_OPTIONS_NEW,
                title: SETTINGS_DISPLAY_EPG_OPTIONS_NEW,
                type: EpgLayout.LIST,
            },
        ],
        [],
    );

    const renderItem = useCallback(
        ({ item }: ListRenderItemInfo<EpgLayoutSettingsItem>) => (
            <SettingsRowItem
                item={item}
                title={t(item.title)}
                onPress={handleChangeAudio}
                RightIcon={
                    selectedEpgLayout === item.type ? CheckMarkImage : undefined
                }
            />
        ),
        [handleChangeAudio, selectedEpgLayout, t],
    );

    useEffect(() => {
        setSelectedEpgLayout(
            epgLayout ??
                (screenReaderEnabled ? EpgLayout.LIST : EpgLayout.GRID),
        );
    }, [epgLayout, screenReaderEnabled]);

    return (
        <View style={[gStyles.settingsContainer, listStyle]}>
            <FlatList
                style={gStyles.settingsContentContainer}
                nestedScrollEnabled
                data={epgLayoutOptions}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                showsHorizontalScrollIndicator={false}
                accessibilityRole="menu"
            />
        </View>
    );
}

export default EpgDisplayOptions;

function keyExtractor({ id }: EpgLayoutSettingsItem) {
    return id;
}
